import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, fetchSmsMediaObjectUrl } from '../api';
import { useVoice } from '../context/VoiceContext';

/** Play a voicemail recording via the authenticated backend proxy (the raw
 *  Twilio recording URL needs account credentials, which would pop a browser
 *  Basic-Auth dialog). */
function VoicemailAudio({ url }: { url: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true;
    let obj: string | null = null;
    fetchSmsMediaObjectUrl(`/voice/recording?url=${encodeURIComponent(url)}`)
      .then((u) => { if (active) { obj = u; setSrc(u); } else URL.revokeObjectURL(u); })
      .catch(() => { if (active) setErr(true); });
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  if (err) return <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>[voicemail unavailable]</span>;
  if (!src) return <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Loading voicemail…</span>;
  return <audio controls src={src} style={{ height: 32, marginTop: 4 }} />;
}

const btn: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  background: 'transparent',
  color: 'var(--color-text)',
  fontWeight: 600,
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
};
const accent = 'var(--color-accent)';
const red = 'var(--color-error)';
const card: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};
const selStyle: React.CSSProperties = { width: '100%', fontSize: 'var(--font-size-xs)', padding: '4px 6px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-background)', color: 'var(--color-text)', boxSizing: 'border-box' };

function fmtWhen(ts: string) {
  return new Date(ts).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function fmtDur(s: number | null) {
  if (!s) return '';
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

const fwInput: React.CSSProperties = { flex: 1, fontSize: 'var(--font-size-sm)', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-background)', color: 'var(--color-text)', boxSizing: 'border-box' };

/** "Ring my phones": manage the mobiles that ring alongside the browser on an
 *  incoming call (so staff are reachable with the site closed), with a master
 *  on/off toggle and add/remove — all self-service, no redeploy. */
function ForwardingSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['voice-forwarding'], queryFn: () => api.getVoiceForwarding() });
  const [newNum, setNewNum] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const enabled = data?.enabled ?? false;
  const numbers = data?.numbers ?? [];
  const toggle = useMutation({ mutationFn: (v: boolean) => api.setVoiceForwardingEnabled(v), onSuccess: (d) => qc.setQueryData(['voice-forwarding'], d) });
  const add = useMutation({
    mutationFn: (n: string) => api.addVoiceForwardNumber(n),
    onSuccess: (d) => { qc.setQueryData(['voice-forwarding'], d); setNewNum(''); setErr(null); },
    onError: (e: any) => setErr(e?.message || 'Could not add that number'),
  });
  const remove = useMutation({ mutationFn: (id: number) => api.removeVoiceForwardNumber(id), onSuccess: (d) => qc.setQueryData(['voice-forwarding'], d) });
  return (
    <details style={{ marginBottom: 'var(--spacing-3)' }}>
      <summary style={{ cursor: 'pointer', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        📱 Ring my phones{numbers.length ? ` (${numbers.length})` : ''}{numbers.length && !enabled ? ' · off' : ''}
      </summary>
      <div style={{ display: 'grid', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-2)', maxWidth: 420 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text)' }}>
          <input type="checkbox" checked={enabled} onChange={(e) => toggle.mutate(e.target.checked)} />
          Also ring these mobiles on an incoming call (works even when this site is closed)
        </label>
        {numbers.length > 1 && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            Phones ring one after another in this order, 25 seconds each. The next one rings only if the one before it was not answered.
          </span>
        )}
        {numbers.map((n, i) => (
          <div key={n.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', padding: '4px var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)' }}>
            <span style={{ fontSize: 'var(--font-size-sm)' }}>
              <span style={{ display: 'inline-block', minWidth: 42, fontSize: 'var(--font-size-xs)', fontWeight: 700, color: accent }}>{i === 0 ? 'Rings 1st' : `then ${i + 1}${i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'}`}</span>
              {' '}{n.e164}{n.label ? ` · ${n.label}` : ''}
            </span>
            <button style={btn} disabled={remove.isPending} onClick={() => remove.mutate(n.id)}>Remove</button>
          </div>
        ))}
        {numbers.length === 0 && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>No numbers yet — add one below.</span>}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
          <input value={newNum} onChange={(e) => setNewNum(e.target.value)} placeholder="04… or +61…" style={fwInput}
                 onKeyDown={(e) => { if (e.key === 'Enter' && newNum.trim()) add.mutate(newNum.trim()); }} />
          <button style={{ ...btn, background: accent, color: '#fff', borderColor: accent }} disabled={!newNum.trim() || add.isPending} onClick={() => add.mutate(newNum.trim())}>Add</button>
        </div>
        {err && <span style={{ color: red, fontSize: 'var(--font-size-xs)' }}>{err}</span>}
      </div>
    </details>
  );
}

/** Phone tab UI (keypad + recents + audio devices). Call control + the incoming
 *  popup live in the app-wide VoiceProvider, so calls ring on any page. */
export function Phone({ initialNumber, onOpenContact, onCreateFromNumber }: {
  initialNumber?: string;
  onOpenContact?: (customerId: number) => void;
  onCreateFromNumber?: (number: string) => void;
}) {
  const navigate = useNavigate();
  const voice = useVoice();
  const [tab, setTab] = useState<'keypad' | 'recents'>(initialNumber ? 'keypad' : 'recents');
  const [number, setNumber] = useState(initialNumber ?? '');
  const [missedOnly, setMissedOnly] = useState(false);
  const { data } = useQuery({ queryKey: ['voice-calls'], queryFn: () => api.listVoiceCalls(), refetchInterval: 15_000 });
  const allCalls = data?.calls ?? [];
  const calls = missedOnly ? allCalls.filter((c) => c.missed) : allCalls;

  if (!voice) return null;
  const { status, edge, callState, error, inputs, outputs, inId, outId, outputSupported, placeCall, chooseMic, chooseSpeaker } = voice;

  if (status === 'not-configured') {
    return <section style={card}><p style={{ color: 'var(--color-text-muted)' }}>The phone isn't set up yet. Add the Twilio Voice API key, secret and TwiML App, then it'll come online here.</p></section>;
  }

  const dialing = callState !== 'idle';
  const tabBtn = (v: typeof tab) => ({ ...btn, ...(tab === v ? { background: accent, color: '#fff', borderColor: accent } : {}) });

  return (
    <section style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-2)' }}>
        <span style={{ fontSize: 'var(--font-size-xs)', color: status === 'ready' ? accent : 'var(--color-text-muted)' }}>
          {status === 'ready' ? `● Phone ready${edge ? ` · ${edge}` : ''}` : status === 'connecting' ? '○ Connecting…' : '○ Phone offline'}
        </span>
        {error && <span style={{ fontSize: 'var(--font-size-xs)', color: red }}>{error}</span>}
      </div>

      {status === 'ready' && (inputs.length > 0 || outputs.length > 0) && (
        <details style={{ marginBottom: 'var(--spacing-3)' }}>
          <summary style={{ cursor: 'pointer', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>🎧 Audio devices</summary>
          <div style={{ display: 'grid', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-2)', maxWidth: 380 }}>
            {inputs.length > 0 && (
              <label style={{ display: 'grid', gap: 2, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                Microphone
                <select value={inId} onChange={(e) => chooseMic(e.target.value)} style={selStyle}>
                  {inputs.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Microphone'}</option>)}
                </select>
              </label>
            )}
            {outputSupported && outputs.length > 0 ? (
              <label style={{ display: 'grid', gap: 2, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                Speaker
                <select value={outId} onChange={(e) => chooseSpeaker(e.target.value)} style={selStyle}>
                  {outputs.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Speaker'}</option>)}
                </select>
              </label>
            ) : (
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Speaker follows your system default (this browser can't switch it here).</span>
            )}
          </div>
        </details>
      )}

      <ForwardingSettings />

      <div style={{ display: 'flex', gap: 'var(--spacing-1)', alignItems: 'center', marginBottom: 'var(--spacing-3)' }}>
        <button style={tabBtn('keypad')} onClick={() => setTab('keypad')}>Keypad</button>
        <button style={tabBtn('recents')} onClick={() => setTab('recents')}>Recents{data?.missed ? ` · ${data.missed} missed` : ''}</button>
        {tab === 'recents' && (
          <button
            onClick={() => setMissedOnly((m) => !m)}
            style={{ ...btn, marginLeft: 'auto', ...(missedOnly ? { background: red, color: '#fff', borderColor: red } : {}) }}
          >{missedOnly ? '● Missed only' : 'Missed only'}</button>
        )}
      </div>

      {tab === 'keypad' ? (
        <div style={{ maxWidth: 260, margin: '0 auto' }}>
          <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="+61…" style={{ width: '100%', textAlign: 'center', fontSize: 'var(--font-size-lg,20px)', padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-background)', color: 'var(--color-text)', boxSizing: 'border-box' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--spacing-2)', margin: 'var(--spacing-3) 0' }}>
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((d) => (
              <button key={d} onClick={() => setNumber((n) => n + d)} style={{ padding: 'var(--spacing-3)', fontSize: 'var(--font-size-lg,20px)', fontWeight: 600, border: '1px solid var(--color-border)', borderRadius: '50%', aspectRatio: '1', background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer' }}>{d}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'center' }}>
            <button disabled={dialing || status !== 'ready' || !number.trim()} onClick={() => placeCall(number)} style={{ ...btn, background: accent, color: '#fff', borderColor: accent, padding: 'var(--spacing-2) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}>📞 Call</button>
            <button style={btn} onClick={() => setNumber((n) => n.slice(0, -1))}>⌫</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 2 }}>
          {calls.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)' }}>{missedOnly ? 'No missed calls.' : 'No calls yet.'}</p>
          ) : (
            calls.map((c) => {
              const external = c.direction === 'in' ? c.fromNumber : c.toNumber;
              const label = c.customerName || external || 'Unknown';
              const openRow = () => {
                if (c.customerId) { onOpenContact ? onOpenContact(c.customerId) : navigate(`/contacts?open=${c.customerId}`); }
                else if (external) { onCreateFromNumber?.(external); }
              };
              const clickable = Boolean(c.customerId || external);
              return (
                <div
                  key={c.id}
                  onClick={clickable ? openRow : undefined}
                  title={c.customerId ? 'Open contact' : external ? 'Add as a contact' : ''}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', padding: 'var(--spacing-2)', borderTop: '1px solid var(--color-border)', cursor: clickable ? 'pointer' : 'default' }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: c.missed ? red : 'var(--color-text)' }}>
                      {c.direction === 'in' ? '↘ ' : '↗ '}
                      <span style={{ color: c.customerId ? (c.missed ? red : 'var(--color-accent)') : 'inherit' }}>{label}</span>
                      {!c.customerId && external && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-accent)', fontWeight: 600 }}> · + Add contact</span>}
                      {c.missed && ' · Missed'}
                      {c.status === 'voicemail' && ' · 🎙 Voicemail'}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      {fmtWhen(c.createdAt)}{c.durationSeconds ? ` · ${fmtDur(c.durationSeconds)}` : ''}
                    </div>
                    {c.voicemailUrl && <div onClick={(e) => e.stopPropagation()}><VoicemailAudio url={c.voicemailUrl} /></div>}
                  </div>
                  {external && (
                    <button style={{ ...btn, borderColor: accent, color: accent }} disabled={dialing || status !== 'ready'} onClick={(e) => { e.stopPropagation(); setNumber(external); placeCall(external); }}>📞 Call</button>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </section>
  );
}
